import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowUp, AlertCircle, BookOpen, Download, FileText, Loader2, Lock } from 'lucide-react';
import { remonterEnHaut } from '../utils/defilement';
import { fetchDocumentation, lireDocument, lienDocument, lienDocumentPdf } from '../services/api';

const poids = (octets: number): string =>
  octets < 1024 ? `${octets} o` : `${Math.round(octets / 1024)} Ko`;

/**
 * La sentinelle qui met les blocs de code à l'abri.
 *
 * Écrite en échappement, et non frappée telle quelle : le caractère nul posé
 * directement dans la source faisait de ce fichier le seul du dépôt que git,
 * grep et les éditeurs tenaient pour binaire — plus de diff lisible, plus de
 * recherche, et un jour un éditeur qui l'aurait réécrit sans le dire.
 */
const SENTINELLE = '\u0000';

/**
 * Rend un texte Markdown en HTML, et rien de plus.
 *
 * Une bibliothèque complète pèserait cinquante kilo-octets pour convertir six
 * guides que nous écrivons nous-mêmes. Ce convertisseur ne connaît que ce que
 * ces guides utilisent : titres, listes, tableaux, blocs de code, filets,
 * citations, gras, italique, liens.
 *
 * Les tableaux manquaient, et le guide de l'encaissement en contient deux : la
 * table des clés Stripe et celle des événements sortaient en lignes de barres
 * verticales, sous les yeux du trésorier qui cherchait où coller sa clé. Les
 * citations, elles, s'empilaient une par ligne — dix-huit encadrés là où
 * l'auteur avait écrit trois paragraphes.
 *
 * Le texte est échappé d'abord, systématiquement, et alors seulement les
 * balises voulues sont réintroduites. C'est l'ordre qui compte : convertir puis
 * échapper laisserait passer exactement ce qu'on cherche à bloquer. Ces
 * documents viennent de nous et non d'un tiers, mais un jour quelqu'un voudra
 * en déposer un, et la discipline aura déjà été prise.
 */
function enHtml(markdown: string): string {
  const echapper = (t: string) =>
    t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  // Les fins de ligne d'abord : les guides sont écrits sous Windows, donc en
  // CRLF, et la clôture des blocs de code cherchait un saut de ligne nu juste
  // après le nom du langage. Elle tombait sur le retour chariot et ne trouvait
  // jamais rien : aucun bloc de code n'a jamais été rendu à l'écran, les
  // exemples sortaient en paragraphes hachés avec leurs accents graves.
  const source = markdown.replace(/\r\n?/g, '\n');

  // Les blocs de code ensuite : leur contenu ne doit subir aucune autre
  // transformation, sans quoi un exemple de commande deviendrait du gras.
  const blocs: string[] = [];
  let texte = source.replace(/```[\w-]*\n([\s\S]*?)```/g, (_, code: string) => {
    blocs.push(`<pre class="bloc-code"><code>${echapper(code.trimEnd())}</code></pre>`);
    return `${SENTINELLE}BLOC${blocs.length - 1}${SENTINELLE}`;
  });

  texte = echapper(texte);

  const enLigne = (t: string) =>
    t
      .replace(/`([^`]+)`/g, '<code>$1</code>')
      .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
      .replace(/(^|[^*])\*([^*\n]+)\*/g, '$1<em>$2</em>')
      // Le lien n'accepte que http, https et mailto : « javascript: » dans
      // un document déposé serait la faille la plus banale qui soit.
      .replace(
        /\[([^\]]+)\]\((https?:\/\/[^)\s]+|mailto:[^)\s]+)\)/g,
        '<a href="$2" target="_blank" rel="noopener">$1</a>'
      );

  /** Les cellules d'une ligne de tableau, barres de bord retirées. */
  const cellules = (ligne: string): string[] =>
    ligne
      .trim()
      .replace(/^\||\|$/g, '')
      .split('|')
      .map((c) => c.trim());

  // C'est la ligne de tirets qui fait le tableau, pas la barre verticale :
  // sans elle, une phrase contenant une barre deviendrait un en-tête.
  const estSeparateur = (ligne: string): boolean =>
    /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/.test(ligne);

  const alignement = (cellule: string): string => {
    const gauche = cellule.startsWith(':');
    const droite = cellule.endsWith(':');
    if (gauche && droite) return ' style="text-align:center"';
    if (droite) return ' style="text-align:right"';
    return '';
  };

  const lignes = texte.split('\n');
  const sortie: string[] = [];
  let listeOuverte: 'ul' | 'ol' | null = null;
  let paragraphe: string[] = [];

  const viderParagraphe = () => {
    if (paragraphe.length > 0) {
      sortie.push(`<p>${enLigne(paragraphe.join(' '))}</p>`);
      paragraphe = [];
    }
  };
  const fermerListe = () => {
    if (listeOuverte) {
      sortie.push(`</${listeOuverte}>`);
      listeOuverte = null;
    }
  };

  for (let i = 0; i < lignes.length; i += 1) {
    const brut = lignes[i].trimEnd();

    if (brut.trim() === '') {
      viderParagraphe();
      fermerListe();
      continue;
    }

    const marqueur = brut.match(new RegExp(`^${SENTINELLE}BLOC(\\d+)${SENTINELLE}$`));
    if (marqueur) {
      viderParagraphe();
      fermerListe();
      sortie.push(blocs[Number(marqueur[1])]);
      continue;
    }

    // Tableau : la ligne d'en-têtes, la ligne de tirets, puis les lignes.
    if (brut.includes('|') && i + 1 < lignes.length && estSeparateur(lignes[i + 1])) {
      viderParagraphe();
      fermerListe();

      const alignements = cellules(lignes[i + 1]).map(alignement);
      const entetes = cellules(brut)
        .map((c, n) => `<th${alignements[n] ?? ''}>${enLigne(c)}</th>`)
        .join('');

      const corps: string[] = [];
      let j = i + 2;
      for (; j < lignes.length && lignes[j].includes('|') && lignes[j].trim() !== ''; j += 1) {
        const cases = cellules(lignes[j])
          .map((c, n) => `<td${alignements[n] ?? ''}>${enLigne(c)}</td>`)
          .join('');
        corps.push(`<tr>${cases}</tr>`);
      }
      i = j - 1;

      // L'enveloppe défile seule : un tableau de quatre colonnes sur un
      // téléphone déborderait sinon la page entière.
      sortie.push(
        `<div class="table-enveloppe"><table><thead><tr>${entetes}</tr></thead>` +
          `<tbody>${corps.join('')}</tbody></table></div>`
      );
      continue;
    }

    // Le filet se teste avant la puce : « --- » commence comme un tiret.
    if (/^\s*(-{3,}|\*{3,}|_{3,})\s*$/.test(brut)) {
      viderParagraphe();
      fermerListe();
      sortie.push('<hr>');
      continue;
    }

    const titre = brut.match(/^(#{1,6})\s+(.*)$/);
    if (titre) {
      viderParagraphe();
      fermerListe();
      const niveau = Math.min(6, titre[1].length + 1); // # devient h2 : le h1 est celui de la page
      sortie.push(`<h${niveau}>${enLigne(titre[2])}</h${niveau}>`);
      continue;
    }

    const puce = brut.match(/^\s*[-*+]\s+(.*)$/);
    if (puce) {
      viderParagraphe();
      if (listeOuverte !== 'ul') {
        fermerListe();
        sortie.push('<ul>');
        listeOuverte = 'ul';
      }
      sortie.push(`<li>${enLigne(puce[1])}</li>`);
      continue;
    }

    const numero = brut.match(/^\s*\d+[.)]\s+(.*)$/);
    if (numero) {
      viderParagraphe();
      if (listeOuverte !== 'ol') {
        fermerListe();
        sortie.push('<ol>');
        listeOuverte = 'ol';
      }
      sortie.push(`<li>${enLigne(numero[1])}</li>`);
      continue;
    }

    // Les lignes de citation qui se suivent forment un seul encadré.
    const citation = brut.match(/^\s*&gt;\s?(.*)$/);
    if (citation) {
      viderParagraphe();
      fermerListe();

      const bloc: string[] = [citation[1]];
      while (i + 1 < lignes.length) {
        const suite = lignes[i + 1].trimEnd().match(/^\s*&gt;\s?(.*)$/);
        if (!suite) break;
        bloc.push(suite[1]);
        i += 1;
      }

      const paragraphes = bloc
        .join('\n')
        .split(/\n\s*\n/)
        .map((p) => p.trim())
        .filter((p) => p !== '')
        .map((p) => `<p>${enLigne(p.split('\n').join(' '))}</p>`)
        .join('');
      sortie.push(`<blockquote>${paragraphes}</blockquote>`);
      continue;
    }

    paragraphe.push(brut.trim());
  }

  viderParagraphe();
  fermerListe();

  return sortie.join('\n');
}

/**
 * Les documents explicatifs de l'espace.
 *
 * Ils existaient — guide du bureau, du bénévole, du sponsor, mise en place de
 * l'encaissement — mais dans le dépôt du logiciel, c'est-à-dire nulle part
 * pour qui s'en sert. Un guide qu'il faut demander n'est pas un guide : c'est
 * une réponse d'assistance qu'on n'a pas encore envoyée.
 *
 * Chacun se lit ici et s'emporte. Le téléchargement n'est pas un doublon de
 * la lecture : un guide se lit à l'écran une fois, puis s'imprime et se pose
 * sur la table le soir de la fête.
 */
export default function DocumentationPage() {
  const [ouvert, setOuvert] = useState<string | null>(null);
  const notice = useRef<HTMLElement>(null);

  const catalogue = useQuery({ queryKey: ['documentation'], queryFn: fetchDocumentation });

  const document = useQuery({
    queryKey: ['document', ouvert],
    queryFn: () => lireDocument(ouvert as string),
    enabled: ouvert !== null,
  });

  // Le guide s'ouvre sous la liste : sans ce déplacement, on clique sur
  // « Lire ici » et rien ne bouge à l'écran — le texte est en dessous, hors
  // du champ, et l'on croit que le bouton n'a pas marché. On attend d'avoir
  // le contenu, sinon on défile vers un cadre encore vide.
  useEffect(() => {
    if (ouvert === null || document.isPending) return;
    notice.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [ouvert, document.isPending]);

  if (catalogue.isPending) {
    return (
      <div className="p-8 flex justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  const documents = catalogue.data?.documents ?? [];

  return (
    <div className="p-4 lg:p-0 space-y-5 pb-24 lg:pb-0">
      <div className="flex items-center gap-3">
        <BookOpen className="w-7 h-7 text-primary-700 shrink-0" />
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-gray-100">
            Guides et documents
          </h1>
          <p className="text-sm lg:text-base text-gray-600 dark:text-gray-400">
            À lire ici, ou à emporter et imprimer.
          </p>
        </div>
      </div>

      {documents.length === 0 ? (
        <div className="card text-center py-10">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Aucun document sur cet espace. La prochaine livraison les apportera.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {documents.map((d) => (
            <div
              key={d.code}
              className={`card space-y-2.5 ${d.ouvert ? '' : 'opacity-60'}`}
            >
              <div className="flex items-start gap-3">
                {d.ouvert ? (
                  <FileText className="w-5 h-5 text-primary-600 shrink-0 mt-0.5" />
                ) : (
                  <Lock className="w-5 h-5 text-gray-400 shrink-0 mt-0.5" />
                )}
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-gray-900 dark:text-gray-100">{d.titre}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {d.pour} · {poids(d.octets)}
                  </p>
                </div>
              </div>

              <p className="text-sm text-gray-600 dark:text-gray-400">{d.resume}</p>

              {d.ouvert ? (
                <div className="flex flex-wrap gap-2">
                  {d.format === 'markdown' ? (
                    <button
                      type="button"
                      onClick={() => setOuvert(ouvert === d.code ? null : d.code)}
                      className="btn-outline text-sm py-1.5"
                    >
                      {ouvert === d.code ? 'Replier' : 'Lire ici'}
                    </button>
                  ) : (
                    <a
                      href={lienDocument(d.code)}
                      target="_blank"
                      rel="noopener"
                      className="btn-outline text-sm py-1.5"
                    >
                      Ouvrir dans un onglet
                    </a>
                  )}
                  {/* Le PDF d'abord, et le texte brut ensuite : c'est le PDF
                      qu'on imprime, qu'on range dans un classeur et qu'on
                      joint à un courriel. Le Markdown ne s'ouvre pas — on
                      obtient une page d'astérisques et l'on croit le lien
                      cassé. */}
                  <a
                    href={lienDocumentPdf(d.code)}
                    className="btn-primary inline-flex items-center gap-2 text-sm py-1.5"
                    download
                  >
                    <Download className="w-4 h-4" /> PDF
                  </a>
                  <a
                    href={lienDocument(d.code)}
                    className="inline-flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 hover:underline"
                    download
                  >
                    texte brut
                  </a>
                </div>
              ) : (
                <p className="text-xs text-gray-500 dark:text-gray-400 flex items-start gap-1.5">
                  <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  Réservé à : {d.pour.toLowerCase()}.
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Le document lu se déplie sous la liste, à pleine largeur : on lit un
          guide, on ne le consulte pas dans une colonne de dix centimètres. */}
      {ouvert && (
        <section ref={notice} className="card lg:p-8 scroll-mt-4">
          {document.isPending ? (
            <div className="p-8 flex justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
            </div>
          ) : document.isError ? (
            <p className="text-sm text-red-700">{(document.error as Error).message}</p>
          ) : (
            <article
              className="prose-guide"
              // Le contenu vient de nos propres fichiers, échappé puis
              // reconstruit par le convertisseur ci-dessus.
              dangerouslySetInnerHTML={{ __html: enHtml(document.data?.contenu ?? '') }}
            />
          )}

          {/* Le retour en haut, au bas du guide.

              Le bouton flottant existe, mais il ne paraît qu'au-delà d'une
              hauteur et demie d'écran, et il flotte au-dessus du texte : on ne
              le cherche pas, on le subit ou on l'ignore. Arrivé au bout d'une
              notice de quinze écrans, ce qu'on veut est une sortie à l'endroit
              où la lecture s'arrête.

              Il ramène en haut de la PAGE, et non en haut du guide : au bout
              d'une notice, ce qu'on cherche est la liste des autres.

              Il ne paraît qu'une fois le guide chargé — sous une roue qui
              tourne ou un message d'erreur, il n'aurait rien à conclure. */}
          {!document.isPending && !document.isError && (
            <div className="mt-8 border-t border-gray-200 dark:border-gray-700 pt-4 flex justify-center">
              <button
                type="button"
                onClick={(e) => remonterEnHaut(e.currentTarget)}
                className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium
                  text-gray-600 dark:text-gray-300 hover:text-primary-700 dark:hover:text-primary-400
                  hover:bg-gray-100 dark:hover:bg-gray-800 transition
                  focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
              >
                <ArrowUp className="w-4 h-4" aria-hidden="true" />
                Revenir en haut de la page
              </button>
            </div>
          )}
        </section>
      )}

    </div>
  );
}
